import { useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from './AuthProvider'
import { useDraftGuard } from './DraftGuard'
import { Icon, type IconName } from './Icon'
import type { Role } from '../types'
import { environment } from '../lib/env'

interface NavigationItem {
  icon: IconName
  label: string
  requiredRole?: Role
  to: string
}

const navItems: NavigationItem[] = [
  { icon: 'dashboard', label: 'Overview', to: '/dashboard' },
  { icon: 'clock', label: 'Time', requiredRole: 'Employee', to: '/time' },
  { icon: 'receipt', label: 'Expenses', requiredRole: 'Employee', to: '/expenses' },
  { icon: 'team', label: 'Approvals', requiredRole: 'Manager', to: '/approvals' },
  { icon: 'settings', label: 'Administration', requiredRole: 'Admin', to: '/admin' }
]

export const hasRole = (roles: Role[], required?: Role) => {
  if (!required) return true
  if (roles.includes('Admin')) return true
  if (required === 'Employee') return roles.includes('Employee') || roles.includes('Manager')
  return roles.includes(required)
}

const initials = (name: string) =>
  name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

export const AppShell = () => {
  const { session, signOut } = useAuth()
  const { confirmNavigation, isDirty } = useDraftGuard()
  const [isMobileOpen, setMobileOpen] = useState(false)
  const location = useLocation()

  if (!session) return null
  const allowedItems = navItems.filter((item) => hasRole(session.user.roles, item.requiredRole))
  const activeItem = allowedItems.find((item) => location.pathname.startsWith(item.to))
  const roleLabel = session.user.roles.includes('Admin')
    ? 'Administrator'
    : session.user.roles.includes('Manager')
      ? 'Manager'
      : 'Employee'

  const leave = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!confirmNavigation()) {
      event.preventDefault()
      return
    }
    setMobileOpen(false)
  }

  return (
    <div className="app-frame">
      <button
        aria-label="Close navigation"
        className={`sidebar-scrim ${isMobileOpen ? 'is-visible' : ''}`}
        onClick={() => setMobileOpen(false)}
      />
      <aside className={`sidebar ${isMobileOpen ? 'is-open' : ''}`} aria-label="Primary navigation">
        <div className="brand-lockup">
          <div className="brand-mark" aria-hidden="true"><span>Q</span></div>
          <div>
            <strong>Quantum</strong>
            <small>TIME &amp; EXPENSE</small>
          </div>
        </div>

        <div className="workspace-label">Workspace</div>
        <nav className="side-nav">
          {allowedItems.map((item) => (
            <NavLink key={item.to} to={item.to} className={({ isActive }) => `side-nav-link ${isActive ? 'active' : ''}`} onClick={leave}>
              <Icon name={item.icon} size={19} />
              <span>{item.label}</span>
              {item.label === 'Approvals' && <span className="nav-count">4</span>}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-bottom">
          <div className="build-card">
            <span className="build-pulse" />
            <div>
              <strong>QTE is ready</strong>
              <small>Build {environment.appVersion}</small>
            </div>
          </div>
          <a className="support-link" href="mailto:technology@quantuminsights.us">
            <Icon name="shield" size={16} /> Privacy &amp; support
          </a>
        </div>
      </aside>

      <div className="app-main">
        <header className="topbar">
          <button className="icon-button mobile-menu" onClick={() => setMobileOpen(true)} aria-label="Open navigation">
            <Icon name="menu" />
          </button>
          <div className="topbar-context">
            <span className="topbar-kicker">{activeItem?.label || 'Workspace'}</span>
            {isDirty && <span className="unsaved-indicator">Unsaved changes</span>}
          </div>
          <div className="topbar-actions">
            <button className="icon-button notification-button" aria-label="Notifications">
              <Icon name="bell" size={19} />
              <span />
            </button>
            <details className="account-menu">
              <summary aria-label="Open account menu">
                <span className="avatar avatar-primary">{initials(session.user.name)}</span>
                <span className="account-name"><strong>{session.user.name}</strong><small>{roleLabel}</small></span>
                <Icon name="chevron-down" size={16} />
              </summary>
              <div className="account-popover">
                <p>{session.user.email}</p>
                <div className="role-pills">
                  {session.user.roles.map((role) => <span key={role}>{role}</span>)}
                </div>
                <button className="menu-action" onClick={() => signOut({ returnToIdentityProvider: !session.isDemo })}>
                  <Icon name="sign-out" size={16} /> Sign out
                </button>
              </div>
            </details>
          </div>
        </header>
        <main className="content-area"><Outlet /></main>
      </div>
    </div>
  )
}
